"use client";

import { useState } from "react";
import { sbBlok } from "@/types/sb-types";
import { prizeCalculatorSeason1 } from "../../utils/prizeCalculators/season1";
import Dropdown from "../Dropdown";
import { TOURNAMENT_IDS_SEASON_1 } from "@/utils/constants";
import clsx from "clsx";

interface prizeType {
  round_1: number;
  round_2: number;
  round_3: number;
  round_4: number;
  round_5: number;
}

interface prizeTableType {
  round_1: number[];
  round_2: number[];
  round_3: number[];
  round_4: number[];
  round_5: number[];
}

interface PrizeListOldProps {
  blok?: sbBlok;
  defaultSpacing?: string;
  tournamentId?: string;
}

const PRIZE_DISTRIBUTION = {
  round_1: 126600,
  round_2: 150600,
  round_3: 150500,
  round_4: 148200,
  round_5: 150600,
};

const TOTAL_PLAYERS = {
  round_1: 267,
  round_2: 506,
  round_3: 505,
  round_4: 482,
  round_5: 506,
};

export const PRIZE_TABLE: { [key: string]: any } = {
  round_1: prizeCalculatorSeason1({
    roundParticipants: 267,
    roundPrizeTotal: 126600,
  }),
  round_2: prizeCalculatorSeason1({
    roundParticipants: 506,
    roundPrizeTotal: 150600,
  }),
  round_3: prizeCalculatorSeason1({
    roundParticipants: 505,
    roundPrizeTotal: 150500,
  }),
  round_4: prizeCalculatorSeason1({
    roundParticipants: 482,
    roundPrizeTotal: 148200,
  }),
  round_5: prizeCalculatorSeason1({
    roundParticipants: 506,
    roundPrizeTotal: 150600,
  }),
  round_6: [
    20000,
    12600,
    8100,
    6300,
    4725,
    3511,
    3009,
    2508,
    2247,
    2006,
    1846,
    1725,
    1615,
    1540,
    1475,
    1414,
    1354,
    1294,
    1244,
    1204,
    1164,
    1134,
    1103,
    1073,
    1043,
    1013,
    983,
    953,
    923,
    893,
    ...Array.from(Array(70)).map(() => 0),
  ],
};

const dummyPrizeListSelectOptions = [...new Array(6)].map((_, index) => {
  return {
    _uid: index + 1,
    id: `round_${index + 1}`,
    name: index === 5 ? "Finale" : `Round ${index + 1}`,
  };
});

const PrizeListSeason1 = ({
  blok,
  defaultSpacing,
  tournamentId,
}: PrizeListOldProps) => {
  const blokData = blok
    ? blok
    : {
        _uid: "",
        component: "",
        select_options: dummyPrizeListSelectOptions,
      };

  const selectOptions = blokData?.select_options?.map((option: sbBlok) => ({
    label: option?.name,
    id: option?._uid,
  }));

  const tournamentIdFirstSeasonIndex = TOURNAMENT_IDS_SEASON_1.indexOf(
    tournamentId as string
  );
  const firstSeasonTournamentRound = `round_${
    tournamentIdFirstSeasonIndex + 1
  }`;

  const [selectedOption, setSelectedOption] = useState(
    tournamentIdFirstSeasonIndex !== -1
      ? tournamentIdFirstSeasonIndex + 1
      : blokData?.select_options[0]._uid
  );

  const currentRound = blokData?.select_options?.find(
    (r: sbBlok) => r._uid === selectedOption
  );

  const [selectedView, setSelectedView] = useState(
    tournamentIdFirstSeasonIndex !== -1
      ? firstSeasonTournamentRound
      : currentRound?.id
  );

  const handleSelectChange = (option: string) => {
    const getRound = blokData?.select_options?.find(
      (r: sbBlok) => r._uid === option
    );
    setSelectedView(getRound.id);
    setSelectedOption(option);
  };

  return (
    <div
      className={clsx("grid-container mt-10 mb-5 2xl:mt-[70px] 2xl:mb-10", {
        "pt-7 lg:pt-14":
          !!blokData.spacing_top?.length &&
          blokData.spacing_top[0].option === "large",
        "pt-14 lg:pt-[120px]":
          !!blokData.spacing_top?.length &&
          blokData.spacing_top[0].option === "x_large",
        "pt-[120px] lg:pt-[180px]":
          !!blokData.spacing_top?.length &&
          blokData.spacing_top[0].option === "xx_large",
        "pb-7 lg:pb-14":
          !!blokData.spacing_bottom?.length &&
          blokData.spacing_bottom[0].option === "large",
        "pb-14 lg:pb-[120px]":
          !!blokData.spacing_bottom?.length &&
          blokData.spacing_bottom[0].option === "x_large",
        "pb-[120px] lg:pb-[180px]":
          !!blokData.spacing_bottom?.length &&
          blokData.spacing_bottom[0].option === "xx_large",
        "pt-14 lg:pt-[120px] pb-14 lg:pb-[120px]": defaultSpacing,
      })}
    >
      <div className="col-start-1 col-span-4 lg:col-start-6 lg:col-span-6 2xl:col-start-12 2xl:col-span-10 mb-[50px]">
        {blokData?.title && <h1 className="text-center">{blokData.title}</h1>}
        <div className="flex justify-center">
          <Dropdown
            defaultValue={{
              label: currentRound?.name,
              id: currentRound?._uid,
            }}
            options={selectOptions}
            onChange={(e: any) => {
              handleSelectChange(e.id);
            }}
          />
        </div>
      </div>
      <div className="col-start-1 col-span-4 lg:col-start-5 lg:col-span-8 2xl:col-start-9 2xl:col-span-16">
        <div className="flex flex-col md:flex-row gap-5 md:gap-36 lg:gap-40 xl:gap-48 p-0 w-full mb-[50px]">
          <div className="flex flex-col gap-2.5">
            <span className="text-4xl 2xl:text-6xl">
              $
              {selectedView === "round_6"
                ? (100000).toLocaleString("en-US")
                : PRIZE_DISTRIBUTION?.[
                    selectedView as keyof prizeType
                  ]?.toLocaleString("en-US")}
            </span>
            <span className="text-xl text-tm-c-gray-300">Purse</span>
          </div>
          <div className="flex flex-col gap-2.5">
            <span className="text-4xl 2xl:text-6xl">
              {selectedView === "round_6"
                ? 100
                : TOTAL_PLAYERS?.[selectedView as keyof {}]}
            </span>
            <span className="text-xl text-tm-c-gray-300">Players</span>
          </div>
        </div>
      </div>
      <div className="col-start-1 col-span-4 lg:col-start-5 lg:col-span-8 2xl:col-start-9 2xl:col-span-16">
        <ol className="list-none m-0 p-0 bg-tm-c-gray-950 rounded overflow-hidden">
          <li className="flex even:bg-[rgba(65,65,65,0.2)] first:bg-tm-c-gray-700 [&>*]:w-full [&>*]:block [&>*]:text-center [&>*]:py-[10px] [&>*]:px-0 [&>*]:text-xl">
            <span>Position</span>
            <span>Prize money</span>
          </li>
          {PRIZE_TABLE[selectedView as keyof prizeTableType]?.map(
            (p: number, i: number) =>
              p > 0 && (
                <li
                  key={i}
                  className="flex even:bg-[rgba(65,65,65,0.2)] first:bg-tm-c-gray-700 [&>*]:w-full [&>*]:block [&>*]:text-center [&>*]:py-[10px] [&>*]:px-0 [&>*]:text-xl"
                >
                  <span>{i + 1}</span>
                  <span>${p.toLocaleString("en-US")}</span>
                </li>
              )
          )}
          {PRIZE_TABLE[selectedView as keyof prizeTableType]?.length > 1 && (
            <li className="flex even:bg-[rgba(65,65,65,0.2)] first:bg-tm-c-gray-700 [&>*]:w-full [&>*]:block [&>*]:text-center [&>*]:py-[10px] [&>*]:px-0 [&>*]:text-xl">
              <span>
                {PRIZE_TABLE[selectedView as keyof prizeTableType]?.indexOf(0) +
                  1}
                ...
                {PRIZE_TABLE[selectedView as keyof prizeTableType]?.length}
              </span>
              <span>$0</span>
            </li>
          )}
        </ol>
      </div>
    </div>
  );
};

export default PrizeListSeason1;
